import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DebitoInmediatoPayment from '@/components/DebitoInmediatoPayment';
import DomiciliacionPayment from '@/components/DomiciliacionPayment';
import OrderInfoCard from '@/components/payment/OrderInfoCard';
import PaymentMethodSelector from '@/components/payment/PaymentMethodSelector';
import PaymentForm from '@/components/payment/PaymentForm';
import {
  LoadingScreen,
  NotFoundScreen,
  PaidScreen,
  ExpiredOrCancelledScreen,
  SuccessScreen,
  FailedScreen,
} from '@/components/payment/PaymentStatusScreens';
import { extractErrorMessage } from '@/components/payment/paymentErrors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_FORM = {
  phoneCode: '0424',
  phone: '',
  reference: '',
  bankCode: '',
  amount: '',
  date: '',
  docType: 'V',
  docNumber: '',
  token: '',
};

export default function PaymentGateway() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [banks, setBanks] = useState([]);
  const [error, setError] = useState('');
  const [paymentForm, setPaymentForm] = useState(DEFAULT_FORM);
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/orders/${orderId}`);
        setOrder(response.data);
        setPaymentForm((prev) => ({
          ...prev,
          amount: response.data.amount.toString(),
          docNumber: response.data.payer_document.replace(/[^0-9]/g, ''),
          docType: response.data.payer_document.charAt(0),
        }));
      } catch (e) {
        console.error('Error loading order:', e);
        setPaymentStatus('error');
      } finally {
        setLoading(false);
      }
    };
    const loadBanks = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/banco-plaza/banks`);
        if (response.data.bancos) setBanks(response.data.bancos);
      } catch (e) {
        console.error('Error loading banks:', e);
      }
    };
    const loadExchangeRate = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/exchange-rate`);
        setExchangeRate(res.data);
      } catch (e) {
        console.warn('Exchange rate not available:', e.message);
      }
    };
    loadOrder();
    loadBanks();
    loadExchangeRate();
  }, [orderId]);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      const paymentData = {
        order_id: orderId,
        payment_method: selectedMethod,
        phoneCode: paymentForm.phoneCode,
        phone: paymentForm.phone,
        reference: paymentForm.reference,
        bankCode: paymentForm.bankCode,
        amount: parseFloat(paymentForm.amount),
        date: paymentForm.date,
        docType: paymentForm.docType,
        docNumber: paymentForm.docNumber,
        token: paymentForm.token || undefined,
      };
      const response = await axios.post(
        `${BACKEND_URL}/api/public/orders/${orderId}/verify-payment`,
        paymentData,
      );
      if (response.data.success) {
        setPaymentStatus('success');
        if (order.success_redirect_url) {
          setTimeout(() => {
            window.location.href = order.success_redirect_url;
          }, 3000);
        }
      } else {
        setPaymentStatus('failed');
        setError(response.data.message || 'Error al verificar pago');
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      setPaymentStatus('failed');
      setError(extractErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleRetry = () => {
    setPaymentStatus('pending');
    setError('');
  };

  if (loading) return <LoadingScreen />;
  if (!order || paymentStatus === 'error') return <NotFoundScreen />;
  if (order.status === 'paid') return <PaidScreen order={order} />;
  if (order.status === 'expired' || order.status === 'cancelled') {
    return <ExpiredOrCancelledScreen status={order.status} />;
  }
  if (paymentStatus === 'success') return <SuccessScreen order={order} />;
  if (paymentStatus === 'failed') return <FailedScreen error={error} onRetry={handleRetry} />;

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{ background: 'linear-gradient(135deg, #051C2C 0%, #003594 50%, #008AD8 100%)' }}
    >
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-block bg-white/95 rounded-xl p-4 shadow-lg">
            <img src="/logos/caura-pay-main.png" alt="Caura Pay" className="h-20 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-white mt-4">Pasarela de Pago Segura</h1>
        </div>

        <OrderInfoCard order={order} exchangeRate={exchangeRate} />

        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <h3 className="font-bold text-lg mb-4" style={{ color: '#051C2C' }}>
            Selecciona Método de Pago
          </h3>

          <PaymentMethodSelector selectedMethod={selectedMethod} onSelect={setSelectedMethod} allowedMethods={order.allowed_payment_methods} />

          {selectedMethod && selectedMethod !== 'immediate_debit' && selectedMethod !== 'domiciliacion' && (
            <PaymentForm
              order={order}
              banks={banks}
              selectedMethod={selectedMethod}
              paymentForm={paymentForm}
              setPaymentForm={setPaymentForm}
              processing={processing}
              onSubmit={handlePaymentSubmit}
            />
          )}

          {selectedMethod === 'immediate_debit' && (
            <DebitoInmediatoPayment
              order={order}
              onSuccess={() => {
                setPaymentStatus('success');
                if (order.success_redirect_url) {
                  setTimeout(() => {
                    window.location.href = order.success_redirect_url;
                  }, 3000);
                }
              }}
              onError={(errorMsg) => {
                setError(errorMsg);
                setPaymentStatus('failed');
              }}
            />
          )}

          {selectedMethod === 'domiciliacion' && (
            <DomiciliacionPayment
              order={order}
              onSuccess={() => {
                setPaymentStatus('success');
                if (order.success_redirect_url) {
                  setTimeout(() => { window.location.href = order.success_redirect_url; }, 3000);
                }
              }}
              onError={(errorMsg) => { setError(errorMsg); setPaymentStatus('failed'); }}
            />
          )}

          {!selectedMethod && (
            <div className="text-center py-8 text-gray-500">
              <p>Selecciona un método de pago para continuar</p>
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <div className="inline-flex items-center justify-center gap-3 bg-white/95 rounded-lg px-6 py-3 shadow-lg">
            <p className="text-gray-700 text-sm font-medium">Pago seguro procesado por</p>
            <img src="/logos/banco-plaza.png" alt="Banco Plaza" className="h-5" />
          </div>
        </div>
      </div>
    </div>
  );
}
